import { API_BASE_URL } from '../../config/api';

interface KakaoPayReadyRequest {
  productId: string;
  quantity?: number;
  taxFreeAmount?: number;
  vatAmount?: number;
}

interface KakaoPayReadyResponse {
  success: boolean;
  paymentId: string;
  tid: string;
  nextRedirectPcUrl: string;
  nextRedirectMobileUrl: string;
  nextRedirectAppUrl?: string;
  androidAppScheme?: string;
  iosAppScheme?: string;
}

interface KakaoCancelRequest {
  paymentId: string;
  cancelAmount: number;
  cancelTaxFreeAmount?: number;
  cancelVatAmount?: number;
  cancelReason?: string;
}

interface PaymentStatusResponse {
  success: boolean;
  paymentId: string;
  status: string;
  amount: {
    total: number;
    taxFree: number;
    vat: number;
    point: number;
    discount: number;
  };
  canceledAmount?: {
    total: number;
    taxFree: number;
    vat: number;
    point: number;
    discount: number;
  };
  cancelAvailableAmount?: {
    total: number;
    taxFree: number;
    vat: number;
    point: number;
    discount: number;
  };
  paymentMethodType?: string;
  itemName?: string;
  createdAt?: string;
  approvedAt?: string;
  canceledAt?: string;
}

class KakaoPayService {
  private baseUrl = `${API_BASE_URL}/payments/kakao`;
  
  /**
   * 카카오페이 결제 준비
   */
  async preparePayment(request: KakaoPayReadyRequest): Promise<KakaoPayReadyResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/ready`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          product_id: request.productId,
          quantity: request.quantity || 1,
          tax_free_amount: request.taxFreeAmount || 0,
          vat_amount: request.vatAmount
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '결제 준비에 실패했습니다');
      }

      const data = await response.json();
      
      // 모바일/PC 환경 감지하여 적절한 URL로 리다이렉트
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      
      const redirectUrl = isMobile 
        ? data.next_redirect_mobile_url 
        : data.next_redirect_pc_url;

      // 결제 페이지로 이동
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }

      return {
        success: data.success,
        paymentId: data.payment_id,
        tid: data.tid,
        nextRedirectPcUrl: data.next_redirect_pc_url,
        nextRedirectMobileUrl: data.next_redirect_mobile_url,
        nextRedirectAppUrl: data.next_redirect_app_url,
        androidAppScheme: data.android_app_scheme,
        iosAppScheme: data.ios_app_scheme
      };
    } catch (error) {
      console.error('Failed to prepare Kakao payment:', error);
      throw error;
    }
  }

  /**
   * 카카오페이 결제 상태 조회
   */
  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/status/${paymentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '결제 상태 조회에 실패했습니다');
      }

      const data = await response.json();
      
      return {
        success: data.success,
        paymentId: data.payment_id,
        status: data.status,
        amount: data.amount,
        canceledAmount: data.canceled_amount,
        cancelAvailableAmount: data.cancel_available_amount,
        paymentMethodType: data.payment_method_type,
        createdAt: data.created_at,
        approvedAt: data.approved_at,
        canceledAt: data.canceled_at
      };
    } catch (error) {
      console.error('Failed to get payment status:', error);
      throw error;
    }
  }

  /**
   * 카카오페이 결제 환불
   */
  async cancelPayment(request: KakaoCancelRequest): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          payment_id: request.paymentId,
          cancel_amount: request.cancelAmount,
          cancel_tax_free_amount: request.cancelTaxFreeAmount || 0,
          cancel_vat_amount: request.cancelVatAmount,
          cancel_reason: request.cancelReason
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '환불 처리에 실패했습니다');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to cancel Kakao payment:', error);
      throw error;
    }
  }

  /**
   * 결제 성공/실패/취소 처리
   * URL 파라미터를 파싱하여 처리
   */
  handlePaymentCallback(): { success: boolean; paymentId?: string; message?: string } {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('payment_id');
    const pgToken = urlParams.get('pg_token');
    const errorCode = urlParams.get('error_code');
    const errorMessage = urlParams.get('error_message');

    // 현재 경로 확인
    const path = window.location.pathname;

    if (path.includes('/payment/kakao/success') && pgToken) {
      // 결제 승인 처리는 백엔드에서 자동으로 처리됨
      return {
        success: true,
        paymentId: paymentId || undefined,
        message: '결제가 성공적으로 완료되었습니다'
      };
    } else if (path.includes('/payment/kakao/cancel')) {
      return {
        success: false,
        paymentId: paymentId || undefined,
        message: '결제가 취소되었습니다'
      };
    } else if (path.includes('/payment/kakao/fail')) {
      return {
        success: false,
        paymentId: paymentId || undefined,
        message: errorMessage || '결제에 실패했습니다'
      };
    }

    return {
      success: false,
      message: '알 수 없는 오류가 발생했습니다'
    };
  }

  /**
   * 결제 준비 후 앱 실행 (모바일 앱 환경)
   */
  launchKakaoPayApp(
    androidAppScheme?: string, 
    iosAppScheme?: string,
    redirectUrl?: string
  ) {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isAndroid && androidAppScheme) {
      // 안드로이드 앱 실행
      window.location.href = androidAppScheme;
      
      // 앱이 설치되어 있지 않은 경우 대체 처리
      setTimeout(() => {
        if (document.hasFocus() && redirectUrl) {
          window.location.href = redirectUrl;
        }
      }, 3000);
    } else if (isIOS && iosAppScheme) {
      // iOS 앱 실행
      window.location.href = iosAppScheme;
      
      // 앱이 설치되어 있지 않은 경우 대체 처리
      setTimeout(() => {
        if (document.hasFocus() && redirectUrl) {
          window.location.href = redirectUrl;
        }
      }, 3000);
    } else if (redirectUrl) {
      // 웹 환경에서는 리다이렉트 URL로 이동
      window.location.href = redirectUrl;
    }
  }
}

export default new KakaoPayService();